/**
 * Local Forklifts booking catalog (Electric / LPG / Diesel sizes).
 */

import {
  BOOKING_REGION_EMAILS,
  bookingCompanyRecipients,
  bookingRegionalEmail,
} from './site_emails.js'

const FORKLIFT_TYPES = {
  Manual: ['Standard'],
  Electric: ['1.0T', '1.5T', '1.8T', '2.0T', '2.5T'],
  LPG: ['1.8T', '2.5T', '3.0T', '3.5T', '4.0T', '5.0T'],
  Diesel: ['1.8T', '2.5T', '3.0T', '3.5T', '4.0T', '5.0T', '7.0T', '10.0T', '12.0T'],
}

const toModels = (labels, prefix) =>
  labels.map((label) => ({
    heading: label,
    slug: `${prefix}-${label.replace(/\s+/g, '-').toLowerCase()}`,
  }))

/** @type {Record<string, Record<string, { heading: string, slug: string }[]>>} */
export const LOCAL_BOOKING_CATALOG = {
  forklifts: Object.fromEntries(
    Object.entries(FORKLIFT_TYPES).map(([type, models]) => [
      type,
      toModels(models, `forklift-${type.toLowerCase()}`),
    ])
  ),
}

export const LOCAL_CATALOG_CATEGORY_KEYS = Object.keys(LOCAL_BOOKING_CATALOG)

const KNOWN_TYPE_LABELS = {
  forklifts: 'Forklifts',
  Manual: 'Manual',
  Electric: 'Electric',
  LPG: 'LPG',
  Diesel: 'Diesel',
}

export function typeLabel(key) {
  if (!key) return ''
  if (KNOWN_TYPE_LABELS[key]) return KNOWN_TYPE_LABELS[key]
  if (!/^[a-z][a-z0-9_]*$/i.test(key)) return key
  return key
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export const CATEGORY_LABELS = {
  forklifts: 'Forklifts',
}

export const HIRE_LOCATIONS = ['Auckland', 'Waikato', 'Christchurch']

export const DURATION_UNITS = ['Days', 'Weeks', 'Months']

export { BOOKING_REGION_EMAILS, bookingCompanyRecipients, bookingRegionalEmail }

export function productParamLabel() {
  return 'Size'
}

export function isCatalogItemValid(item, catalog) {
  if (!item?.category || !item.qty) return false
  const types = Object.keys(catalog[item.category] || {})
  if (types.length > 0 && !item.type) return false
  if (item.type === 'Manual') return true
  if (!item.model) return false
  return true
}

export function buildCategoryOptions() {
  return [
    { value: '', label: 'Select Equipment Category' },
    { value: 'forklifts', label: 'Forklifts', order: 1 },
  ]
}

export function buildProductsByCategory() {
  return { ...LOCAL_BOOKING_CATALOG }
}

/**
 * Prefill from ?equipment= — keep Category/Type/Size editable.
 * Matches known Electric/LPG/Diesel + size tokens when present in the string.
 */
export function prefillItemFromEquipment(equipment) {
  if (!equipment) return null
  const text = String(equipment)
  const lower = text.toLowerCase()

  let type = ''
  if (/\belectric\b/i.test(text) || /\bfb[- ]?series\b/i.test(lower)) type = 'Electric'
  else if (/\blpg\b/i.test(text) || /\bpetrol\b/i.test(text)) type = 'LPG'
  else if (/\bdiesel\b/i.test(text) || /\bic\b/i.test(text)) type = 'Diesel'

  let model = ''
  if (type) {
    const sizes = FORKLIFT_TYPES[type] || []
    const sizeMatch = text.match(/(\d+(?:\.\d+)?)\s*[tT]/)
    if (sizeMatch) {
      const normalized = `${Number(sizeMatch[1]).toFixed(1)}T`
      if (sizes.includes(normalized)) model = normalized
      else {
        const alt = `${sizeMatch[1]}T`
        if (sizes.includes(alt)) model = alt
      }
    }
  }

  return {
    category: 'forklifts',
    type,
    model,
  }
}
